// City-level geocoding with Firestore cache — Google Maps Geocoding API only
// City + State only - no street addresses
import { db } from './firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { geocodeLocation } from './geocodeLocation';
import { isValidUsLocation } from './maps/usLocationValidation';

export interface GeoCoordinates {
  lat: number;
  lng: number;
}

export interface GeoCacheEntry {
  key: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  createdAt?: any;
  updatedAt?: any;
}

const CACHE_COLLECTION = 'geoCache';

/** Geocode city+state via shared geocodeLocation (Maps JS in browser, REST fallback). */
async function geocodeCityState(city: string, state: string): Promise<GeoCoordinates | null> {
  const query = state ? `${city}, ${state}, USA` : `${city}, USA`;
  const result = await geocodeLocation(query);
  if (!result.ok) {
    if (import.meta.env.DEV) console.warn('[geoCache] geocode failed:', query, result.reason);
    return null;
  }
  return { lat: result.lat, lng: result.lng };
}

/** Firestore docId: US_${state}_${cityLower} — city-level only, no address. */
export function geoCacheKey(city: string, state: string): string {
  const c = (city || '').trim().toLowerCase().replace(/\s+/g, '_');
  const s = (state || '').trim().toUpperCase().slice(0, 2);
  if (!c) return 'unknown';
  return `US_${s || 'XX'}_${c}`;
}

/**
 * Get or compute geo for city+state. Google Maps Geocoding only; no Nominatim.
 * Returns null if city/state invalid, API key missing, or geocode fails.
 */
export async function getGeoForCityState(city: string, state: string): Promise<GeoCoordinates | null> {
  const c = (city || '').trim();
  const s = (state || '').trim();
  if (!isValidUsLocation(c, s)) return null;

  const key = geoCacheKey(c, s);
  if (!db) {
    if (import.meta.env.DEV) console.warn('[geoCache] Firestore not initialized');
    return geocodeCityState(c, s);
  }

  try {
    const ref = doc(db, CACHE_COLLECTION, key);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data() as GeoCacheEntry;
      if (typeof d.lat === 'number' && typeof d.lng === 'number') {
        return { lat: d.lat, lng: d.lng };
      }
    }

    const coords = await geocodeCityState(c, s);
    if (!coords) return null;

    const updatedAt = serverTimestamp();
    try {
      await setDoc(ref, {
        key,
        city: c,
        state: s,
        lat: coords.lat,
        lng: coords.lng,
        createdAt: updatedAt,
        updatedAt,
      } as Omit<GeoCacheEntry, 'createdAt' | 'updatedAt'> & { createdAt: any; updatedAt: any });
    } catch (writeErr: any) {
      if (import.meta.env.DEV) {
        console.warn('[geoCache] Failed to cache geo:', writeErr?.code, writeErr?.message);
      }
    }

    return coords;
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[geoCache] Error:', err);
    return null;
  }
}
